import * as React from "react";
import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import URI from "urijs";
import useLocation from "routing/useLocation";
import useQuery from "routing/useQuery";
import DashboardPageContext from "views/components/contexts/DashboardPageContext";
import useViewsSelector from "views/stores/useViewsSelector";
import { selectViewStates } from "views/logic/slices/viewSelectors";
import useViewsDispatch from "views/stores/useViewsDispatch";
import { selectQuery } from "views/logic/slices/viewSlice";
const _clearURI = (query) => new URI(query).removeSearch("page");
const _updateQueryParams = (newPage, query) => {
  const baseUri = _clearURI(query);
  if (newPage) {
    return baseUri.setSearch("page", newPage).toString();
  }
  return baseUri.toString();
};
const useSyncStateWithQueryParams = ({ dashboardPage, uriParams, setDashboardPage }) => {
  const states = useViewsSelector(selectViewStates);
  const dispatch = useViewsDispatch();
  useEffect(() => {
    const nextPage = uriParams.page;
    if (!(states == null ? void 0 : states.has(nextPage))) {
      setDashboardPage(void 0);
    } else if (nextPage !== dashboardPage) {
      setDashboardPage(nextPage);
      dispatch(selectQuery(nextPage));
    }
  }, [uriParams.page, dashboardPage, setDashboardPage, states, dispatch]);
};
const useCleanupQueryParams = ({ uriParams, query, navigate }) => {
  useEffect(() => {
    if ((uriParams == null ? void 0 : uriParams.page) === void 0) {
      const baseURI = _clearURI(query);
      const newQuery = baseURI.toString();
      if (query !== newQuery) {
        navigate(newQuery, { replace: true });
      }
    }
  }, [query, navigate, uriParams == null ? void 0 : uriParams.page]);
};
const DashboardPageContextProvider = ({ children }) => {
  const { search, pathname } = useLocation();
  const query = pathname + search;
  const navigate = useNavigate();
  const [dashboardPage, setDashboardPage] = useState();
  const params = useQuery();
  const uriParams = useMemo(
    () => ({
      page: params.page
    }),
    [params]
  );
  useSyncStateWithQueryParams({ dashboardPage, uriParams, setDashboardPage });
  useCleanupQueryParams({ uriParams, query, navigate });
  const dashboardPageContextValue = useMemo(() => {
    const updatePageParams = (newPage) => {
      const newUri = _updateQueryParams(newPage, query);
      navigate(newUri, { replace: true });
    };
    const setDashboardPageParam = (nextPage) => updatePageParams(nextPage);
    const unSetDashboardPageParam = () => updatePageParams(void 0);
    return {
      setDashboardPage: setDashboardPageParam,
      unsetDashboardPage: unSetDashboardPageParam,
      dashboardPage
    };
  }, [dashboardPage, navigate, query]);
  return /* @__PURE__ */ React.createElement(DashboardPageContext.Provider, { value: dashboardPageContextValue }, children);
};
export default DashboardPageContextProvider;
